import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Package, Plus, Trash2, Check, X, Pencil } from "lucide-react";
import { api, typeBadge } from "../lib/dimsum";

const inputCls =
  "w-full rounded-2xl border-2 border-stone-300 bg-stone-50 px-4 py-3 text-base font-semibold text-stone-900 focus:border-rose-500 focus:bg-white focus:outline-none transition-colors";

const CategoryPill = ({ isJumbo }) => (
  <span
    className={`inline-block rounded-lg px-2 py-0.5 text-xs font-bold ${
      isJumbo ? "bg-orange-100 text-orange-700" : "bg-amber-100 text-amber-700"
    }`}
  >
    {isJumbo ? "Adonan Jumbo" : "Adonan Mini"}
  </span>
);

export const KelolaBarang = ({ onChanged }) => {
  const [items, setItems] = useState([]);
  const [code, setCode] = useState("");
  const [isJumbo, setIsJumbo] = useState(true);
  const [editCode, setEditCode] = useState(null);
  const [newName, setNewName] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/barang").then((r) => setItems(r.data || []));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const refresh = () => {
    load();
    onChanged?.();
  };

  const add = async () => {
    if (!code.trim()) return toast.error("Isi kode/nama barang");
    setBusy(true);
    try {
      await api.post("/barang", { code: code.trim(), is_jumbo: isJumbo });
      toast.success(`✅ Barang "${code.trim()}" ditambahkan`);
      setCode("");
      setIsJumbo(true);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menambah barang");
    } finally {
      setBusy(false);
    }
  };

  const startEdit = (it) => {
    setEditCode(it.code);
    setNewName(it.code);
  };

  const saveRename = async (it) => {
    const nc = newName.trim();
    if (!nc) return toast.error("Kode baru wajib diisi");
    if (nc === it.code) {
      setEditCode(null);
      return;
    }
    setBusy(true);
    try {
      await api.put(`/barang/${encodeURIComponent(it.code)}/rename`, { new_code: nc });
      toast.success(`✅ "${it.code}" diubah menjadi "${nc}" (data lama ikut diperbarui)`);
      setEditCode(null);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal mengubah nama");
    } finally {
      setBusy(false);
    }
  };

  const setCategory = async (it, jumbo) => {
    if (it.is_jumbo === jumbo) return;
    try {
      await api.put(`/barang/${encodeURIComponent(it.code)}/category`, { is_jumbo: jumbo });
      toast.success(`✅ ${it.code} → ${jumbo ? "Adonan Jumbo" : "Adonan Mini"}`);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal mengubah jenis");
    }
  };

  const del = async (it) => {
    if (!window.confirm(`Hapus barang "${it.code}"?`)) return;
    try {
      await api.delete(`/barang/${encodeURIComponent(it.code)}`);
      toast.success("🗑️ Barang dihapus");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menghapus");
    }
  };

  return (
    <div className="grid lg:grid-cols-[340px_1fr] gap-6" data-testid="kelola-barang-page">
      <div className="bg-white border-2 border-stone-200 rounded-3xl p-6 shadow-sm h-fit space-y-4">
        <h3 className="font-bold text-lg text-stone-900 flex items-center gap-2">
          <Package className="w-5 h-5 text-rose-600" /> Tambah Barang
        </h3>
        <input
          data-testid="input-new-barang"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && add()}
          placeholder="Kode/nama barang (mis. ARJ)"
          className={inputCls}
        />
        <div>
          <label className="block mb-1 text-xs font-bold uppercase tracking-wider text-stone-500">Jenis Barang</label>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              data-testid="new-barang-jumbo"
              onClick={() => setIsJumbo(true)}
              className={`min-h-[44px] rounded-2xl font-bold text-sm transition-colors ${
                isJumbo ? "bg-orange-600 text-white shadow-md" : "bg-stone-100 text-stone-700 hover:bg-stone-200"
              }`}
            >
              Adonan Jumbo
            </button>
            <button
              type="button"
              data-testid="new-barang-mini"
              onClick={() => setIsJumbo(false)}
              className={`min-h-[44px] rounded-2xl font-bold text-sm transition-colors ${
                !isJumbo ? "bg-amber-500 text-white shadow-md" : "bg-stone-100 text-stone-700 hover:bg-stone-200"
              }`}
            >
              Adonan Mini
            </button>
          </div>
        </div>
        <button
          data-testid="btn-save-barang"
          onClick={add}
          disabled={busy}
          className="w-full inline-flex items-center justify-center gap-2 min-h-[48px] rounded-2xl bg-rose-600 hover:bg-rose-700 text-white font-bold disabled:opacity-50 active:scale-95 transition-transform"
        >
          <Plus className="w-4 h-4" /> Simpan Barang
        </button>
        <p className="text-sm font-semibold text-stone-500">
          Barang di sini dipakai di Input Rekap & Laporan. Mengubah nama akan otomatis memperbarui data lama.
        </p>
      </div>

      <div data-testid="barang-list" className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4 h-fit">
        {items.map((it) => (
          <div key={it.code} data-testid={`barang-card-${it.code}`} className="bg-white border-2 border-stone-200 rounded-3xl p-4 shadow-sm space-y-3">
            <div className="flex items-center justify-between gap-2">
              {editCode === it.code ? (
                <div className="flex items-center gap-1 flex-1">
                  <input
                    data-testid={`barang-rename-input-${it.code}`}
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && saveRename(it)}
                    className="flex-1 min-w-0 rounded-xl border-2 border-stone-200 px-2 py-1.5 font-bold"
                  />
                  <button data-testid={`barang-rename-save-${it.code}`} onClick={() => saveRename(it)} disabled={busy} className="grid place-items-center w-9 h-9 rounded-lg bg-emerald-600 text-white shrink-0">
                    <Check className="w-4 h-4" />
                  </button>
                  <button onClick={() => setEditCode(null)} className="grid place-items-center w-9 h-9 rounded-lg bg-stone-100 text-stone-700 shrink-0">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <>
                  <span className={`inline-block rounded-lg border px-3 py-1 text-sm font-extrabold ${typeBadge(it.code)}`}>
                    {it.code}
                  </span>
                  <div className="flex items-center gap-1">
                    <button data-testid={`barang-edit-${it.code}`} onClick={() => startEdit(it)} title="Ubah nama" className="grid place-items-center w-9 h-9 rounded-lg text-sky-600 hover:bg-sky-50">
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button data-testid={`barang-delete-${it.code}`} onClick={() => del(it)} title="Hapus" className="grid place-items-center w-9 h-9 rounded-lg text-rose-600 hover:bg-rose-50">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </>
              )}
            </div>
            <div>
              <div className="mb-1.5"><CategoryPill isJumbo={it.is_jumbo} /></div>
              <div className="grid grid-cols-2 gap-2">
                <button
                  data-testid={`barang-cat-jumbo-${it.code}`}
                  onClick={() => setCategory(it, true)}
                  className={`min-h-[38px] rounded-xl text-xs font-bold transition-colors ${
                    it.is_jumbo ? "bg-orange-600 text-white" : "bg-stone-100 text-stone-600 hover:bg-stone-200"
                  }`}
                >
                  Jumbo
                </button>
                <button
                  data-testid={`barang-cat-mini-${it.code}`}
                  onClick={() => setCategory(it, false)}
                  className={`min-h-[38px] rounded-xl text-xs font-bold transition-colors ${
                    !it.is_jumbo ? "bg-amber-500 text-white" : "bg-stone-100 text-stone-600 hover:bg-stone-200"
                  }`}
                >
                  Mini
                </button>
              </div>
            </div>
          </div>
        ))}
        {items.length === 0 && <p className="text-stone-400 font-semibold">Belum ada barang.</p>}
      </div>
    </div>
  );
};
